import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Loader, Button, Flex, Tooltip, InfoIcon, Input, Card, CardHeader, Dropdown, CardBody, Pill } from '@fluentui/react-northstar';
import { ExcelColorIcon, ArrowUpIcon } from '@fluentui/react-icons-northstar';
import RevenueService from "../../../services/revenueservice";
import DemoUserFilterDialog from './demoUserFilterDialog';
import '../microsoftbilling/microsoftbilling.scss';
import { OPERATIONSTATUS } from '../../../shared/appcomponents/enums';
import { RenderTooltipCell } from "../../../shared/appcomponents/TooltipWithCopy"

const PAGE_SIZE = 10;

const DemoUserDetails = (props) => {
    const { acquireToken, viewStatus } = props;
    const [gridData, setGridData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [skip, setSkip] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [dataState, setDataState] = useState({
        sort: [{ field: "Product", dir: "asc" }],
        take: PAGE_SIZE,
        skip: 0,
    });

    const fetchGridData = useCallback((token, filters = {}) => {
        setLoaded(false);
        // Debugging output
        console.log('Fetching grid data with filters:', filters);

        const filterPayload = {
            ...filters,
        };

        if (Object.keys(filterPayload).some(key => filterPayload[key] !== null)) {
            RevenueService.GetDemoUserGridDataFiltered(token, filterPayload)
                .then((response) => {
                    if (response && response.status === 200) {
                        setGridData(response.data);
                        setFilteredData(response.data.slice(skip, skip + PAGE_SIZE));
                        /* viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Success); */
                    } else {
                        viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Failed);
                    }
                    setLoaded(true);
                })
                .catch((error) => {
                    console.error('Error fetching Microsoft Billing data:', error);
                    setLoaded(true);
                });
        } else {
            RevenueService.GetDemoUsersGridData(token)
                .then((response) => {
                    if (response && response.status === 200) {
                        setGridData(response.data);
                        setFilteredData(response.data.slice(0, PAGE_SIZE));
                        setLoaded(true);
                        /* viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Success); */
                    } else {
                        viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Failed);
                    }
                    setLoaded(true);
                })
                .catch((error) => {
                    console.error('Error fetching Microsoft Billing data:', error);
                    setLoaded(true);
                });
        }
    }, [viewStatus, skip]);


    const resetGridData = useCallback((token) => {
        RevenueService.GetDemoUsersGridData(token) // Fetch data without filters
            .then((response) => {
                if (response && response.status === 200) {
                    setGridData(response.data);
                    setFilteredData(response.data.slice(skip, skip + PAGE_SIZE));
                    viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Success);
                } else {
                    viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Failed);
                }
            })
            .catch((error) => {
                console.error('Error fetching grid data:', error);
                viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Failed);
            });
    }, [skip]);

    useEffect(() => {
        acquireToken((token) => {
            fetchGridData(token, appliedFilters); // Use applied filters directly
        });
    }, [acquireToken, appliedFilters, skip, fetchGridData]);

    useEffect(() => {
       acquireToken((token) => {
           // Set default filters only if none are applied
           if (Object.keys(appliedFilters).length === 0) {
               setAppliedFilters({
                   Product: "Cx",
                   StartDate: null,
                   EndDate: null,
               });
           }
           fetchGridData(token, appliedFilters); // Pass current filters
       });
   }, [acquireToken, appliedFilters, fetchGridData]); // Re-run when filters change

    useEffect(() => {
        setFilteredData(gridData.slice(skip, skip + PAGE_SIZE));
    }, [gridData, skip]);

    const handleFilterUpdate = (filters) => {
        const filterPayload = {
            Product: filters.Product || null,
            Customer: filters.Customer || null,
            StartDate: filters.StartDate || null,
            EndDate: filters.EndDate || null,
        };

        // Update applied filters
        setAppliedFilters(filterPayload);

        acquireToken((token) => {
            RevenueService.GetDemoUserGridDataFiltered(token, filterPayload) // Fetch data based on filters
                .then((response) => {
                    if (response.status === 200) {
                        setGridData(response.data);
                        setFilteredData(response.data.slice(0, PAGE_SIZE));
                        setSkip(0); // Resetting pagination
                        viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Success);
                    } else {
                        console.error('Error fetching filtered data:', response.statusText);
                        viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Failed);
                    }
                })
                .catch((error) => {
                    console.error('Error in API call:', error);
                });
        });
    };

    const handleDownloadCsv = () => {
       console.log('Downloading CSV with filters:', appliedFilters);
       setDownloading(true);
       acquireToken((token) => {
           RevenueService.DownloadDemoUsersCSV(token, appliedFilters) // Use the current appliedFilters
               .then((response) => {
                   if (response.status === 200) {
                       const url = window.URL.createObjectURL(new Blob([response.data]));
                       const link = document.createElement('a');
                       link.href = url;
                       link.setAttribute('download', 'Demo_User.csv');
                       document.body.appendChild(link);
                       link.click();
                       document.body.removeChild(link);
                       viewStatus.DisplayOperationStatus(OPERATIONSTATUS.Success);
                   } else {
                       console.error('Error downloading CSV:', response.statusText);
                       viewStatus.DisplayOperationStatus(OPERATIONSTATUS.NoDownload);
                   }
               })
               .catch((error) => {
                   console.error('Error during API call:', error);
                   viewStatus.DisplayOperationStatus(OPERATIONSTATUS.NoDownload);
               })
               .finally(() => {
                   setDownloading(false);
               });
       });
   };



    const handlePageChange = (event) => {
        const newSkip = event.page.skip;
        setSkip(newSkip);
        const newFilteredData = gridData.slice(newSkip, newSkip + PAGE_SIZE);
        setFilteredData(newFilteredData);
    };

    const filterDialog = DemoUserFilterDialog({
        handleFilterUpdate,
        elegibleFilters: ['Product', 'startDate', 'endDate'], // Include "Date"
        acquireToken,
        resetGridData
    });

    const RenderDefaultCell = (props) => {
        const value = props.dataItem[props.field];
        return (
            <td style={{ whiteSpace: 'nowrap' }}>
                {value || 'N/A'} {/* Show "N/A" if value is null/undefined/empty */}
            </td>
        );
    };


    const handleSearch = (query) => {
        setSearchQuery(query.toLowerCase());
        const filtered = gridData.filter(item =>
            Object.keys(item).some(key =>
                String(item[key]).toLowerCase().includes(query.toLowerCase())
            )
        );
        setFilteredData(filtered.slice(skip, skip + PAGE_SIZE));
        setSkip(0); // Reset pagination after search
    };

    const RenderDateCell = (props) => {
        const value = props.dataItem[props.field];
        return (
            <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <Tooltip
                    content={value || 'N/A'}
                    trigger={<span>{value || 'N/A'}</span>}
                />
            </td>
        );
    };

    function dynamicSort(property, order = 'asc') {
        let sortOrder = 1;
        if (order === 'desc') {
            sortOrder = -1;
        }
        return function (a, b) {
            // Handle null/undefined or special strings
            let valueA = a[property] === "N/A" || a[property] == null ? "" : a[property];
            let valueB = b[property] === "N/A" || b[property] == null ? "" : b[property];

            // Compare dates correctly
            if (property === "DateDeleted" && valueA !== "" && valueB !== "") {
                valueA = new Date(valueA);
                valueB = new Date(valueB);
            }

            if (property === "SubscriptionStartDate" && valueA !== "" && valueB !== "") {
                valueA = new Date(valueA);
                valueB = new Date(valueB);
            }

            // Numeric and string comparison
            if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
                valueA = Number(valueA);
                valueB = Number(valueB);
            } else {
                valueA = String(valueA).toLowerCase();
                valueB = String(valueB).toLowerCase();
            }

            return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * sortOrder;
        };
    }

    // Example implementation for a header cell with sorting icons
    useEffect(() => {
        if (gridData.length > 0 && dataState.sort.length > 0) {
            const sortedData = [...gridData].sort(dynamicSort(dataState.sort[0].field, dataState.sort[0].dir));
            setFilteredData(sortedData.slice(skip, skip + PAGE_SIZE));
        }
    }, [gridData, dataState, skip]);

    const handleSortChange = (event) => {
        setDataState({ ...dataState, sort: event.sort });
    };

    const handleDataStateChange = (e) => {
        setDataState(e.data); // Update the data state with the new sorting configuration
    };

    const isEndDateToday = (endDate) => {
        if (!endDate) return false; // No end date provided
        const selectedEndDate = new Date(endDate);
        const currentDate = new Date();
        return (
            selectedEndDate.getFullYear() === currentDate.getFullYear() &&
            selectedEndDate.getMonth() === currentDate.getMonth() &&
            selectedEndDate.getDate() === currentDate.getDate()
        );
    };


    function formatDate(dateString) {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const options = { year: 'numeric', month: 'long', day: '2-digit' };
        return date.toLocaleDateString(undefined, options);
    }
    return (
        <div className="microsoft-billing-dashboard">

            <Flex space="between" className="filter-pill-container">
                {filterDialog}
                <Flex>
                    <div>
                        <Flex>
                            {/* <Dropdown
                                items={currencyOptions}
                                value={selectedCurrency}
                                onChange={(event, data) => setSelectedCurrency(data.value)}
                            /> */}
                            <Input
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            {appliedFilters && (
                                <div className='icnTrk ml-2 download-icon'>
                                    {downloading ? (
                                        <Loader size="smallest" />
                                    ) : (
                                        <Button
                                            onClick={handleDownloadCsv}
                                            icon={<ExcelColorIcon outline rotate={0} size="large" />}
                                            disabled={downloading}
                                            content={downloading ? "Downloading..." : "Download CSV"}
                                        />
                                    )}
                                </div>
                            )}
                        </Flex>
                    </div>
                </Flex>
            </Flex>
            <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '1rem' }}>
                <Grid
                    data={filteredData}
                    skip={skip}
                    pageable={true}
                    pageSize={PAGE_SIZE}
                    total={gridData.length}
                    onPageChange={handlePageChange}
                    resizable
                    onSortChange={handleSortChange}
                    onDataStateChange={handleDataStateChange}
                    sortable={true}
                    sort={dataState.sort}
                >
                    <Column
                        field="EmailId"
                        title="Email Id"
                        width="250px"
                        cell={RenderTooltipCell}
                    />
                    <Column
                        field="DisplayName"
                        title="Name"
                        width="150px"
                        cell={RenderTooltipCell}
                    />
                    {/* <Column
                        field="TenantName"
                        title="Tenant Name"
                        width="80px"
                        cell={RenderTooltipCell}
                    /> */}
                    <Column
                        field="CustomerName"
                        title="Customer Name"
                        width="120px"
                        cell={RenderTooltipCell}
                    />
                    
                        <Column
                            field="DateCreated"
                            title="Date Created"
                            width="150px"
                            cell={RenderTooltipCell}
                        />
                    
                    <Column
                        field="LastLogin"
                        title="Last Login"
                        width="140px"
                        cell={RenderTooltipCell}
                    />
                </Grid>
            </div>
            {!loaded && (
                <div className="loading-indicator">
                    <Loader type="infinite-spinner" />
                    Loading data...
                </div>
            )}
        </div>
    );
};

export default DemoUserDetails;
